import { calculateNationalInsurance } from './nationalInsuranceCalculations';
import { round2 } from './utils/calculationUtils';

/**
 * Calculates additional National Insurance due under 2029 salary sacrifice rules.
 *
 * 2029 Rule: Salary sacrifice pension contributions above £2,000 per tax year
 * become subject to National Insurance.
 *
 * Implementation: We treat the chargeable portion (contributions above £2,000) as
 * additional NI-able income and pass it through the standard NI banding system,
 * using the employee's post-sacrifice NI-able earnings as the starting point.
 * This ensures proper progressive taxation and avoids regressive outcomes.
 *
 * @param {number} salarySacrificeContribution - The total annual salary sacrifice pension contribution
 * @param {number} grossSalary - The employee's gross annual salary (before salary sacrifice)
 * @param {Object} config - The tax year configuration object
 * @returns {number} - Additional NI to be paid under 2029 rules
 */
export const calculate2029SalarySacrificeNI = (
  salarySacrificeContribution,
  grossSalary,
  config
) => {
  // Safety checks
  if (!config || !config.PENSIONS || !config.NI) {
    return 0;
  }

  const threshold = config.PENSIONS.NI_SALARY_SACRIFICE_THRESHOLD;

  // If threshold not defined in config, return 0
  if (threshold === undefined || threshold === null) {
    return 0;
  }

  // If contribution is at or below threshold, no additional NI
  if (salarySacrificeContribution <= threshold) {
    return 0;
  }

  // Calculate the excess contribution above the threshold (the chargeable amount)
  const chargeableAmount = salarySacrificeContribution - threshold;

  // Calculate the employee's post-sacrifice NI-able earnings
  const postSacrificeSalary = grossSalary - salarySacrificeContribution;

  // Calculate NI on the post-sacrifice salary (this is their standard NI)
  const standardNI = calculateNationalInsurance(postSacrificeSalary, config);

  // Calculate NI on (post-sacrifice salary + chargeable amount)
  // This treats the chargeable amount as additional income going through NI bands
  const niWithChargeableAmount = calculateNationalInsurance(
    postSacrificeSalary + chargeableAmount,
    config
  );

  // The 2029 surcharge is the difference between these two NI calculations
  const surcharge = niWithChargeableAmount - standardNI;

  return round2(surcharge);
};
